package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/suspend")
@Composable
fun SuspendPage() {
    BlogLayout(
        PageProperties(
            "suspendCoroutine",
            ""
        ),
        BlogProperties("Coroutines", "suspendCoroutine | suspendCancellableCoroutine | Exception Handling (Throw, resumeWithException) | Callback (resumeWithException)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Problem Statement: Create a Kotlin coroutine function that performs a synchronous call while waiting for a callback. The function should suspend the coroutine until the callback is invoked, and then resume the coroutine with the callback result. The goal is to achieve a synchronous behaviour within the asynchronous context of coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To convert the callback-based function to a coroutine, we need to transform it into a suspend function. We can achieve this by using the suspendCoroutine() or suspendCancellableCoroutine() builder functions provided by Kotlin coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Callbacks - Handling Exception (continuation.resumeWithException())",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Wrapping callback-based APIs, we don’t control when an operation completes. Here, suspendCoroutine { continuation -> ... } helps bridge the gap, allowing us to manually resume a coroutine with success or failure. Callbacks are functions passed as parameters to other functions. We typically use them to handle the result of an asynchronous operation. When the asynchronous task is completed, the callback function is invoked, often with the result as an argument. Callback-based code can become complex and hard to maintain, especially when dealing with multiple asynchronous operations or error handling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "We have followed the following steps to convert the Callback to Coroutines in Kotlin:",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Create a suspend function to return the Result.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use suspendCoroutine as the return block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use continuation.resume(result) for the success.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use continuation.resumeWithException(throwable) for the error.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun doSomething(): Result {
                        return suspendCancellableCoroutine { continuation ->
                            val id = Library.doSomething(object : Listener {

                                override fun onSuccess(result: Result) {
                                    continuation.resume(result)
                                }

                                override fun onError(throwable: Throwable) {
                                    continuation.resumeWithException(throwable)
                                }

                            })

                            continuation.invokeOnCancellation {
                                Library.cancel(id)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "throw in Kotlin Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "The throw keyword is the standard way to raise exceptions in Kotlin. It works inside both normal and suspending functions and is used when we simply need to terminate execution due to an error. If apiCall() returns null, an IllegalStateException is thrown, stopping execution. The coroutine will be canceled if this exception is unhandled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun fetchData(): String {
                        val result = apiCall() ?: throw IllegalStateException("Data not found")
                        return result
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "throw vs. resumeWithException()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Feature throw exception resumeWithException(exception) Where it is used Anywhere in normal or suspending functions Inside suspendCoroutine or suspendCancellableCoroutine How it works Immediately throws an exception Resumes a suspended coroutine with an exception Use Case Standard exception handling Wrapping callback-based APIs",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Choosing Between suspendCoroutine and suspendCancellableCoroutine.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Use suspendCoroutine when you need basic conversion of a callback-based operation to a suspendable function and can manage cancellation manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use suspendCancellableCoroutine when cancellation is an important aspect and you want to leverage built-in cancellation mechanisms and cleanup logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "suspendCoroutine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "It is a function that suspends the execution of a coroutine. It is used to execute asynchronous code within a coroutine.  Converts a callback-style asynchronous operation into a suspendable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines, you can use the suspendCoroutine function to perform a synchronous call while waiting for a callback. This function allows you to suspend the coroutine until the callback is invoked, effectively creating a synchronous behaviour.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Behavior",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Launches a new coroutine to handle the callback-based operation.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Suspends the current coroutine until the callback is invoked.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The callback is responsible for resuming the coroutine with the result or an exception.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Cancellation: Requires manual cancellation logic within the callback or the launched coroutine to properly clean up resources.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                     suspend fun downloadFile(url: String): ByteArray {
                         return suspendCoroutine { continuation ->
                             val request = URL(url).openConnection() as HttpURLConnection
                             request.setRequestProperty("Accept", "application/octet-stream")
                             request.connectAsync { connection ->
                                 if (connection.responseCode == HttpURLConnection.HTTP_OK) {
                                     val inputStream = connection.inputStream
                                     val bytes = inputStream.readBytes()
                                     continuation.resume(bytes) // Resume with downloaded bytes
                                 } else {
                                     continuation.resumeWithException(IOException("Download failed"))
                                 }
                             }
                         }
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun performAsyncOperation(callback: (String) -> Unit) {
                        // Perform an asynchronous operation that invokes the callback
                        // Simulating a delay
                        Thread.sleep(1000)
                        callback("Callback result")
                    }

                    suspend fun performSynchronousCall(): String {
                        return suspendCoroutine { continuation ->
                            // Perform an asynchronous operation that invokes the callback
                            performAsyncOperation { result ->
                                // Resume the coroutine with the callback result
                                continuation.resume(result)
                            }
                        }
                    }

                    fun main() {
                        val job = GlobalScope.launch {
                            val result = performSynchronousCall()
                            println(result)
                        }

                        // Wait for the coroutine to complete
                        runBlocking {
                            job.join()
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the performSynchronousCall function, suspendCoroutine suspends the coroutine until the callback is invoked. The provided lambda function takes a continuation parameter, which you can use to resume the coroutine with the callback result using the resume function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the performSynchronousCall function is invoked in the coroutine, it will wait for the callback to be invoked and then continue executing with the result.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thus, suspendCoroutine is a function that allows you to bridge between callback-based APIs and suspend functions in coroutines. It allows you to manually suspend a coroutine and resume it later when a specific condition is met, typically when a callback is invoked. It is useful when you need to work with existing callback-based APIs that don't have built-in support for coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Multiple Suspend Functions in ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Need to load several pieces of data in parallel inside your ViewModel (like user info, posts, and notifications)?. You can use async within viewModelScope.launch to run multiple suspend functions concurrently — and then await their results before updating your UI state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                   
                    suspend fun fetchUser): User 
                    suspend fun fetchPosts): List<Post> 
                    suspend fun fetchNotificationsCount): Int
                    
                    class YourViewModel: ViewModel) {
                        fun LoadDashboardData() = viewModelScope.launch {
                            coroutineScope {
                                val userDeferred = async { fetchUser () }
                                val postsDeferred = async { fetchPosts) }
                                val notificationsDeferred = async { fetchNotificationsCount()}
                  
                                uiState = viState.copy(
                                    user = userDeferred.await()
                                    posts = postsDeferred.await(),
                                    notifications = notificationsDeferred.await()
                                }
                            }
                    }    }            
                    """.trimIndent()
            )

            SpanText(
                text = "suspendCancellableCoroutine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "suspendCancellableCoroutine is used to convert callback-based APIs into suspend functions that work with coroutines. It creates a bridge between the callback world and coroutine world.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Similar to suspendCoroutine, but provides built-in cancellation support. This is useful when you want to provide the ability to cancel the asynchronous operation and clean up resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "We have three tasks (Task A, Task B, and Task C). Each task takes some time to complete, and we want to ensure that one task finishes before the next one starts. suspendCancellableCoroutine is that it allows us to suspend the coroutine until the task completes, avoiding blocking the main thread while keeping the code clean and readable. Fetching data from multiple APIs in sequence. Processing data in stages, where each stage depends on the result of the previous one. Managing sequential UI transitions or animations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Behavior",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Creates a suspending function and uses a CancellableContinuation object.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Suspends the current coroutine until the callback is invoked or the coroutine is cancelled.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The callback can either resume the coroutine or cancel it.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Offers an invokeOnCancellation callback to handle cleanup logic when cancelled.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                    import com.google.android.gms.tasks.Task
                    import kotlinx.coroutines.suspendCancellableCoroutine
                    import kotlin.coroutines.resumeWithException
                    
                    @OptIn(kotlinx.coroutines.ExperimentalCoroutinesApi::class)
                    suspend fun <T> Task<T>.await(): T{
                        return suspendCancellableCoroutine { cont ->
                            addOnCompleteListener {
                                if(it.exception != null){
                                    cont.resumeWithException(it.exception!!)
                                }else{
                                    cont.resume(it.result, null)
                                }
                            }
                        }
                    }
                    
                    suspend fun loginUser(email: String, password: String): Flow<Resource<AuthResult>> {
                        return flow {
                            emit(Resource.Loading())
                            val result = firebaseAuth.signInWithEmailAndPassword(
                                email, password
                            ).await()
                            emit(Resource.Success(result))

                        }.catch {
                            emit(Resource.Error(it.message.toString()))
                        }
                    }
                    
                    sealed class Resource<T>(val result: T? = null, val message: String? = null){
                        class Success<T>(data: T?): Resource<T>(data)
                        class Error<T>(message: String, data: T? = null): Resource<T>(data, message)
                        class Loading<T>(): Resource<T>(null)
                    }
                    
                    class LoginUsecase @Inject constructor(
                        private val authRepository: AuthRepository,
                    ) {
                        suspend fun execute(email: String, password: String): Flow<Resource<AuthResult>> = authRepository.loginnUser(email, password)
                    }
                    
                    suspend fun login(email: String, password: String) = viewModelScope.launch {
                        loginUsecase.execute(email, password).collect{result ->
                            when(result){
                                is Resource.Success -> {
                                    _state.value = _state.value.copy(loginError = null, isLoginSuccess = true)
                                }
                                is Resource.Loading -> {
                                }
                                is Resource.Error -> {
                                    _state.value = _state.value.copy(loginError = result.message, )
                                }
                            }

                        }
                    }
                    
                    data class LoginState(
                        val email: String = "",
                        val emailError: Int? = null,

                        val password: String = "",
                        val passwordError: Int? = null,

                        val loginError: String? = null,
                        val isLoginSuccess: Boolean = false
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     suspend fun downloadFileWithCancellation(url: String): ByteArray {
                         return suspendCancellableCoroutine { continuation ->
                             val request = URL(url).openConnection() as HttpURLConnection
                             request.setRequestProperty("Accept", "application/octet-stream")

                             continuation.invokeOnCancellation {
                                 request.disconnect() // Cleanup on cancellation
                             }

                             request.connectAsync { connection ->
                                 if (connection.responseCode == HttpURLConnection.HTTP_OK) {
                                     val inputStream = connection.inputStream
                                     val bytes = inputStream.readBytes()
                                     continuation.resume(bytes) // Resume with downloaded bytes
                                 } else {
                                     continuation.resumeWithException(IOException("Download failed"))
                                 }
                             }
                         }
                     }
                """.trimIndent()
            )

            SpanText(
                text = "Choosing Between suspendCoroutine and suspendCancellableCoroutine.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Use suspendCoroutine when you need basic conversion of a callback-based operation to a suspendable function and can manage cancellation manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use suspendCancellableCoroutine when cancellation is an important aspect and you want to leverage built-in cancellation mechanisms and cleanup logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

